import { MetricsDashboardCode, MetricsQueryParameters } from 'shared/models/metrics';
import { ExtendServicePath, ExtendSubdomainService } from 'shared/models/service';
import { crossTeamToggles } from 'config/toggles';
import { session } from 'modules/core/auth/session';
import { OrgLevelPermission } from 'modules/core/components/OrgProvider';
import {
  ApphubAppQueryParameters,
  ApphubPromotionLevel,
  ApphubSourceType,
  ArchiveExcludeType,
} from 'modules/core/models/apphubApps';
import { getOctoDevsiteId } from 'modules/core/utils/devsite';
import { getTenantAuthToken, getTenantAuthTokenWithOrgId } from '../auth/TenantAuth';
import { HeaderKeys, MimeType } from './Definitions';
import DevsiteRequestExecutor from './DevsiteRequestExecutor';
import MaltRequestExecutor from './MaltRequestExecutor';
import OrchestrationBuilderRequestExecutor from './OrchestrationBuilderRequestExecutor';
import { RegionalRequestExecutor } from './RegionalRequestExecutor';
import { RequestExecutor } from './RequestExecutor';
import StudioBuilderRequestExecutor from './StudioBuilderRequestExecutor';
import { aosAuthenticationInterceptor, authenticationInterceptor } from './interceptors';
import { getQueryParameters } from './utils';

// https://ghe.megaleo.com/pages/wcp/octopaas/
export const OctoPaasEndpoint = {
  APP_ROTATE_SECRET: (clientId: string) => `/v1/app/${encodeURIComponent(clientId)}/rotate-secret`,
  ASSOCIATE_APP: (organizationUuid: string) => `/v1/organizations/${encodeURIComponent(organizationUuid)}/apps`,
  CLIENT_SCOPES: (clientId: string) => `/v1/scopes/${encodeURIComponent(clientId)}`,
  BRIDGENBUS_USER: `/v1/bridgenbus/user`,
  BRIDGENBUS_USER_REMOVE: (userUuid: string, organizationUuid: string) =>
    `/v1/bridgenbus/user/${encodeURIComponent(userUuid)}/${encodeURIComponent(organizationUuid)}`,
  BRIDGENBUS_CREDENTIAL: (organizationUuid: string) =>
    `/v1/bridgenbus/account/credential/${encodeURIComponent(organizationUuid)}`,
  BRIDGENBUS_SAML: (organizationUuid: string) => `/v1/bridgenbus/saml/initiate/${encodeURIComponent(organizationUuid)}`,
  BRIDGENBUS_AWS_ACCOUNT_STATUS: (organizationUuid: string) =>
    `/v1/bridgenbus/account/${encodeURIComponent(organizationUuid)}`,
  BRIDGENBUS_ACCOUNT: '/v1/bridgenbus/account',
  BRIDGENBUS_ACCOUNT_DELETE: (orgUuid: string) => `/v1/bridgenbus/aws/account/${encodeURIComponent(orgUuid)}`,
  BRIDGENBUS_ACCOUNT_REVERT_DELETE: (orgUuid: string) => `/v1/bridgenbus/account/${encodeURIComponent(orgUuid)}`,
  DEV_TENANT: '/v1/provisioner/tenant',
  DEV_TENANT_DEL: (orgUuid: string, tenantName: string) => `/v1/provisioner/tenant/${orgUuid}/${tenantName}`,
  DEV_TENANT_PWORD_RESET: '/v1/provisioner/tenant/reset-password',
  DEV_TENANT_GRANT_TTL_EXCEPTION: '/v1/provisioner/tenant/ttl/exception',
  DEV_TENANTS: (orgUuid: string) => `/v1/provisioner/tenant/${orgUuid}`,
  LAST_MODIFIED_USERS: (region: string) => (start: number, length: number) =>
    `/v1/apps/${getOctoDevsiteId(region)}/users?sort=lastModified&start=${start}&length=${length}`,
  LOGOUT: '/v1/logout',
  ORG: (orgUuid: string) => `/v1/organizations/${encodeURIComponent(orgUuid)}`,
  ORG_CLIENTS: (orgUuid: string) => `/v1/organizations/${encodeURIComponent(orgUuid)}/apps`,
  ORG_ENTITLEMENTS: (orgUuid: string) => `/v1/organizations/${encodeURIComponent(orgUuid)}/entitlements`,
  ORG_ENTITLEMENTS_UNRESTRICTED: (orgUuid: string) =>
    `/v1/organizations/${encodeURIComponent(orgUuid)}/entitlements?includeRestricted=false`,
  ORG_ENTITLEMENTS_OVERRIDE: (orgUuid: string) =>
    `/v1/organizations/${encodeURIComponent(orgUuid)}/entitlements/overrides`,
  ORG_FEATURES: (orgUuid: string) => `/v1/organizations/${encodeURIComponent(orgUuid)}/features`,
  OPEN_ACCESS_BETA_MEMBERSHIP: (orgUuid: string) =>
    `/v1/organizations/${encodeURIComponent(orgUuid)}/program-membership`,

  ORG_MEMBER: (email: string, orgUuid: string) =>
    `/v1/users/${encodeURIComponent(email)}/organizations/${encodeURIComponent(orgUuid)}`,
  ORG_USER_STATUS: (orgUuid: string, userUuid: string) =>
    `/v1/organizations/${encodeURIComponent(orgUuid)}/users/${encodeURIComponent(userUuid)}`,
  ORG_USERS: (orgUuid: string) => `/v1/organizations/${encodeURIComponent(orgUuid)}/users`,
  ORGS_WITH_PARMS: (start: number, length: number, type?: string) =>
    `/v1/organizations?start=${start}&length=${length}${type ? `&organizationType=${type}` : ''}`,
  PERMISSIONS: (region: string) => `/v1/apps/${getOctoDevsiteId(region)}/permissions`,
  PERMISSION: (permission: string, region: string) =>
    `/v1/apps/${getOctoDevsiteId(region)}/permissions/${encodeURIComponent(permission)}`,
  PERMISSION_TO_ROLE: (role: string, region: string) =>
    `/v1/apps/${getOctoDevsiteId(region)}/roles/${role}/permissions`,
  PERMISSION_DELETE_FROM_ROLE: (permission: OrgLevelPermission, role: string, region: string) =>
    `/v1/apps/${getOctoDevsiteId(region)}/roles/${encodeURIComponent(role)}/permissions/${encodeURIComponent(
      permission
    )}`,
  PRODUCER_ORG_APP_LICENSES: (orgUuid: string, appRefId: string) => `/v1/license/licensor/${orgUuid}/${appRefId}`,
  PRODUCER_ORG_LICENSE_DETAILS: (orgUuid: string) => `/v1/license/licensor/${orgUuid}`,
  REGIONS: (offset: number, limit: number) => `/v1/regions?offset=${offset}&limit=${limit}`,
  ROLES: (region: string) => `/v1/apps/${getOctoDevsiteId(region)}/roles`,
  ROLES_POST: '/v1/roles',
  ROLES_PUT: (roleName: string, region: string) =>
    `/v1/apps/${getOctoDevsiteId(region)}/roles/${encodeURIComponent(roleName)}`,
  ROLE: (role: string, region: string) => `/v1/apps/${getOctoDevsiteId(region)}/roles/${encodeURIComponent(role)}`,
  SCOPES: '/v1/scopes',
  SCOPES_CLIENT: (clientId: string) => `/v1/scopes/${encodeURIComponent(clientId)}`,
  SELF_APPS: (email: string) => `/v1/app?userId=${encodeURIComponent(email)}`,
  SELF_USER: '/v1/users/self',
  SUPPORT: '/v1/sfdc',
  SUPPORT_COOKIE: '/v1/sfdcCookie',
  TENANTS_FOR_ORG: (orgId: string) => `/v1/organizations/${encodeURIComponent(orgId)}/tenants`,
  TOKEN_REQUEST: '/v1/token',
  USER: (id: string | number) => `/v1/users/${id}`,
  USER_BY_EMAIL: (email: string, region: string) =>
    `/v1/apps/${getOctoDevsiteId(region)}/users/${encodeURIComponent(email)}`,
  USER_DELETE_BY_UUID: (uuid: string) => `/v1/users/${uuid}`,
  USER_IN_ORG: (userUuid: string, orgUuid: string) =>
    `/v1/users/${encodeURIComponent(userUuid)}/organizations/${encodeURIComponent(orgUuid)}`,
  USER_ORGS: (userUuid: string) => `/v1/users/${encodeURIComponent(userUuid)}/organizations`,
  REMOVE_USER: (userUuid: string, orgUuid: string) =>
    `/v1/organizations/${encodeURIComponent(orgUuid)}/users/${encodeURIComponent(userUuid)}`,
  TENANT_CONFIG: (tenantAlias: string) => `/v1/tenantConfig/${encodeURIComponent(tenantAlias)}`,
  USERS: '/v1/users',
  DIST_APP_LICENSE_APP: (consumerOrgUuid: string) => `/v1/license/${consumerOrgUuid}`,
  DIST_APP_LICENSE_INFO: (consumerOrgUuid: string, appRefId: string) =>
    `/v1/license/licensee/${consumerOrgUuid}/${appRefId}`,
  DIST_APP_LICENSEE: (consumerOrgUuid: string) => `/v1/license/licensee/${consumerOrgUuid}`,
  CREATE_BILLING_ID_ASSOCIATION: '/v1/organizations/associate',
  DELETE_BILLING_ID_ASSOCIATION: (orgUuid: string) => `/v1/organizations/${orgUuid}/associate`,
  GET_BILLING_ID_ASSOCIATIONS: (orgUuid: string, lookup: 'parent' | 'child') =>
    `/v1/organizations/${orgUuid}/associate?lookupBy=${lookup}`,
  ORG_BY_BILLING_ID: (billingId: string) => `/v1/organizations/billings/${billingId}`,
  ALL_PRODUCTS: (offset: number = 0, limit: number = 100) => `/v1/products?offset=${offset}&limit=${limit}`,
  PRODUCT: (name: string) => `/v1/products/${encodeURIComponent(name)}`,
  ALL_FEATURES: (offset: number = 0, limit: number = 100) => `/v1/features?offset=${offset}&limit=${limit}`,
  FEATURE: (name: string) => `/v1/features/${encodeURIComponent(name)}`,
  FEATURE_MATRIX: (name: string) => `/v1/features/${encodeURIComponent(name)}/matrix`,
};

export const OctoPaasEndpointV2 = {
  ACCESS_POLICIES: (packageUuid: string) => `/v2/packages/${packageUuid}/accessPolicies`,
  CLIENT_SELF_PERMISSIONS: (clientId: string) => `/v2/clients/${encodeURIComponent(clientId)}/users/self/permissions`,
  CLIENT: (clientId: string) => `/v2/clients/${encodeURIComponent(clientId)}`,
  CLIENTS: '/v2/clients',
  MANAGE_DISABLE_USERS: (uuid: string) => `/v2/users/${uuid}/manageDisable`,
  ORG_ENTITLEMENTS_PACKAGES: (orgUuid: string) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/entitlements/packages`,
  ORG_PRODUCTS: (orgUuid: string) => `/v2/organizations/${encodeURIComponent(orgUuid)}/products`,
  ORG_PERMISSIONS: (orgUuid: string, userUuid: string) => `/v2/organizations/${orgUuid}/users/${userUuid}/permissions`, // can also take string 'self' for userUuid
  ORGANIZATIONS_BY_PACKAGE_UUID: (packageUuid: string) => `/v2/packages/${packageUuid}/organizations`,
  ORGS: '/v2/organizations',
  PACKAGE_BY_UUID: (packageUuid: string) => `/v2/packages/${packageUuid}`,
  PACKAGES_PERMISSION: (packagePermissionName: string) => `/v2/packages/permissions/${packagePermissionName}`,
  PACKAGES_PERMISSIONS_WITH_PARAMS: (offset: number, limit: number) =>
    `/v2/packages/permissions?offset=${offset}&limit=${limit}`,
  PACKAGES_PERMISSIONS: '/v2/packages/permissions',
  PACKAGES_WITH_PARAMS: (offset: number, limit: number) => `/v2/packages?offset=${offset}&limit=${limit}`,
  PACKAGES: '/v2/packages',
  RESET_MFA: (userUuid: string) => `/v2/users/${userUuid}/resetTotp`,
  SELF_ORGS: '/v2/organizations/self',
  USER_SELF_ORGS: '/v2/users/self/organizations',
  SELF_USER_TERMS: '/v2/users/self/terms',
  ORG_ADMINS: (orgUuid: string) => `/v2/organizations/${encodeURIComponent(orgUuid)}/admins`,
  ORG_MEMBERS_WITH_PARAMS: (orgUuid: string) => (start: number, length: number) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/members?offset=${start}&limit=${length}`,
  ORG_NAME: (orgSourceId: string, orgTargetId: string) =>
    `/v2/organizations/${encodeURIComponent(orgSourceId)}/name?targetOrg=${orgTargetId}`,
  ORG_USERS: (orgUuid: string) => `/v2/organizations/${encodeURIComponent(orgUuid)}/users`,
  ORG_USER_UUID: (orgUuid: string, userUuid: string) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/users/${userUuid}`,
  ORG_USERS_WITH_PARAMS: (orgUuid: string) => (start: number, length: number) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/users?offset=${start}&limit=${length}`,
  ORG_USER_ROLE: (orgUuid: string, userUuid: string) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/users/${encodeURIComponent(userUuid)}/roles`,
  ORG_SAML: (orgUuid: string) => `/v2/organizations/${orgUuid}/saml`,
  ORG_SAML_ENDPOINTS: (orgUuid: string) => `/v2/organizations/${orgUuid}/samlEndpoints`,
  ORG_SAML_TOGGLE: (orgUuid: string) => `/v2/organizations/${orgUuid}/samlEnablement`,
  ORG_SAML_ATTRIBUTES: (orgUuid: string) => `/v2/organizations/${orgUuid}/saml/attributes`,
  ORG_SAML_ATTRIBUTE: (orgUuid: string, name: string) =>
    `/v2/organizations/${orgUuid}/saml/attributes/${encodeURIComponent(name)}`,
  ORG_ROLE_BY_UUID: (orgUuid: string, userUuid: string) =>
    `/v2/organizations/${encodeURIComponent(orgUuid)}/users/${userUuid}/roles`,
  USER_BY_UUID: (uuid: string) => `/v2/users/${uuid}`,
  USER_ROLES: (userUuid: string) => `/v2/users/${encodeURIComponent(userUuid)}/roles`,
  USER_ROLE: (userUuid: string, roleName: string) =>
    `/v2/users/${encodeURIComponent(userUuid)}/roles/${encodeURIComponent(roleName)}`,
  APP_USER_ROLES: (appId: string, userUuid: string) => `/v2/apps/${appId}/users/${encodeURIComponent(userUuid)}/roles`,
  GET_CONSENTS_BY_API_CLIENT: (clientId: string, orgUuid: string) =>
    `/v2/clients/${encodeURIComponent(clientId)}/consents/${orgUuid}`,
  CREATE_CONSENT: '/v2/consents',
  DELETE_CONSENT: (lookupAlias: string, clientId: string) =>
    `/v2/consents/tenantConfig/${lookupAlias}/clients/${clientId}`,
  GET_CONSENT: (lookupAlias: string) => `/v2/tenantConfig/${lookupAlias}/consents`,
  TERMS_BY_UUID: (uuid: string) => `/v2/users/${uuid}/terms`,
  USER_TERMS_RESET: (uuid: string) => `/v2/users/${uuid}/terms/reset`,
  TENANT_CONFIGS: '/v2/tenantConfig', // all tenants for currently logged-in user,
  CREATE_SYSTEM_ACCOUNT: '/v2/clients/system',
  DELETE_SYSTEM_ACCOUNT: (clientId: string) => `/v2/clients/system/${clientId}`,
  PRODUCT_ORGANIZATIONS: (name: string) => `/v2/products/${encodeURIComponent(name)}/organizations`,
};

// https://ghe.megaleo.com/pages/wcp/apphub/
export const ApphubEndpoint = {
  APP_CREATE: '/v1/apps',
  APP_VERSION_CREATE: (appId: string) => `/v1/apps/${appId}/versions`,
  APPS: (queryOptions: ApphubAppQueryParameters) => (offset: number, length: number) =>
    `/v1/apps${getQueryParameters({
      ...queryOptions,
      offset,
      limit: length,
    })}`,
  APP: (id: string, params?: { promotionLevel?: ApphubPromotionLevel[] }) =>
    `/v1/apps/${id}${getQueryParameters(params)}`,
  APP_BY_REF: (refId: string, params?: { promotionLevel?: ApphubPromotionLevel; includeRetiredApp?: boolean }) =>
    `/v1/apps/Reference_Id=${refId}${getQueryParameters(params)}`,
  APP_AVAIL_BY_REF: (refId: string, location?: string) =>
    `/v1/appAvailability/Reference_Id=${refId}${getQueryParameters({ timezone: location })}`,
  APP_VERSIONS: (id: string, params?: { limit?: number; promotionLevel?: ApphubPromotionLevel[] }) =>
    `/v1/apps/${id}/versions${getQueryParameters(params)}`,
  APP_VERSIONS_PAGINATED: (refId: string, promotionLevel?: ApphubPromotionLevel[]) => (offset: number, limit: number) =>
    `/v1/apps/Reference_Id=${refId}/versions${getQueryParameters({
      offset,
      limit,
      promotionLevel,
    })}`,
  VERSION: (versionId: string) => `/v1/versions/${versionId}`,
  APP_INSTALLS_BY_REF: (refId: string, orgId?: string, limit: number = 100) =>
    `/v1/gar/installations?referenceId=${refId}${orgId ? '&organizationId=' + orgId : ''}&limit=${limit}`,
  APP_INSTALLS_BY_TENANT: (organizationUuid: string, tenantGwUrl: string, tenantName: string) =>
    `/v1/gar/installations?organizationId=${organizationUuid}&tenantGateway=${tenantGwUrl}&tenantName=${tenantName}`,
  APP_BUILDS: (
    versionId: string,
    params?: { desc?: boolean; includeProdBuilds?: boolean; limit?: number; offset?: number }
  ) => `/v1/versions/${versionId}/builds${getQueryParameters(params)}`,
  BUILD_BY_ID: (buildId: string) => `/v1/builds/${buildId}`,
  BUILD_LOG_THROUGH_APP_HUB: (buildId: string) => `/v1/builds/${buildId}/logs/download`,
  SOURCE: (versionId: string, type?: ApphubSourceType, params?: { name?: string; filename?: string }) =>
    `/v1/versions/${versionId}/source${type ? `/${type}${getQueryParameters(params)}` : ''}`,
  VERSION_FULL_SOURCE: (versionId: string) => `/v1/versions/${versionId}/sourceArchive`,
  BUILD_FULL_SOURCE: (buildId: string) => `/v1/builds/${buildId}/sourceArchive`,
  PROMOTE: (versionId: string) => `/v1/versions/${versionId}/promote`,
  VERSION_PRONTO_DEPLOY: (versionId: string) => `/v1/versions/${versionId}/prontoDeploy`,
  MANIFEST_ID: (versionId: string) => `/v1/versions/${versionId}/manifest`,
  PRONTO_DEPLOY_STATUS: (queryOptions: ApphubAppQueryParameters) =>
    `/v1/prontoStatus/deployment${getQueryParameters({
      ...queryOptions,
    })}`,
  DIST_APP_REVIEW_VERSION: (versionId: string) => `/v1/versions/${versionId}/review`,
  DIST_APP_CERTIFY_VERSION: (versionId: string) => `/v1/versions/${versionId}/certify`,
  DATA_CENTER_LOCATION: (envId: string) => `/v1/dclocation/${envId}`,
  LICENSED_APPS: (queryOptions: ApphubAppQueryParameters) => (offset: number, length: number) =>
    `/v1/licensedApps${getQueryParameters({
      ...queryOptions,
      offset,
      limit: length,
    })}`,
  LICENSED_APP: (refId: string, organizationId: string) =>
    `/v1/licensedApps/Reference_Id=${refId}?organizationId=${organizationId}`,
  APP_RETIRE: (appId: string) => `/v1/apps/${appId}/retire`,
  APP_UNRETIRE: (appId: string) => `/v1/apps/${appId}/unretire`,
};

export const AosEndpoint = {
  REGISTER: '/v1/registry',
  DEPLOY: '/v1/deploy',
};

export const AsorInternalEndpoint = {
  REGISTER_AGENT_BUILDER_DEFINITION: '/v1/agentBuilderDefinition',
};

export const MaltEndpoint = {
  USERS_SELF: '/_workday/v1/users/self',
  USAGE_SELF: '/_workday/v1/usages/self',
  USER_AUDIT_LOGS: '/_workday/v1/admin/useraudit',
  CUSTOMER_LOGS: (fromDate: string, toDate: string) =>
    `/_workday/v1/admin/archive?fromDate=${fromDate}&toDate=${toDate}`,
  CUSTOMER_LOGS_EARLIEST_DATE: (orgUuid: string) =>
    `/_workday/v1/admin/earliestDateForIndex/${orgUuid}?pattern=*.logs.*`,
  DOWNLOAD_ARCHIVE: '/v1/admin/download/archive',
  SCHEMAS: (schemaName: string) => `/v1/logs/schemas/${schemaName}`,
  SEARCH: '/v1/logs/search',
  ADMIN_AUDIT_LOGS: '/v1/admin/logs/search',
  FILTER_SUGGESTIONS: '/v1/logs/filter/suggestions',
  INVOCATIONS_QUERY: '/v1/metrics/query',
  METRICS: (dashboardId: MetricsDashboardCode, queryOptions: MetricsQueryParameters) =>
    `/v2/logs/metrics/dashboard/${dashboardId}${getQueryParameters({
      ...queryOptions,
    })}`,
};

export const MaltEndpointV2 = {
  USERS_SELF: '/_workday/v2/users/self',
};

// changing something? Make sure you check session.ts as well
export const DevsiteEndpoints = {
  SEARCH: '/v1/search',
  REGIONS: '/v1/regions',
};

export const DevsiteEndpointsV2 = {
  SEARCH: '/v2/search',
  PRODUCT_UPDATES: (maxUpdates: number) => `/v2/product-updates?max=${maxUpdates}`,
  RESET_FORUM_CACHE: '/v2/reset-product-updates',
};

const appBuilderV1Path = '/v1/appbuilder';
const appsV1Path = '/v1/apps';
const copilotV1Path = '/copilot/v1';
const devAgentV1Path = '/devagent/v1';
const metadataV1Path = '/v1/metadata';
const draftsV1Path = '/v1/drafts';
const wmdV1Path = '/v1/wmd';
const devtoolsAuthV1Path = '/v1/auth';
const devtoolsAuthV2Path = '/v2/auth';
const devtoolsClientV1Path = '/v1/clients';
const prismV1Path = '/v1/prism';

export const AppBuilderEndpoint = {
  BUILD: `${appBuilderV1Path}/source/build`,
  LIMITS: (appUuid: string) => `${appBuilderV1Path}/${appUuid}/limits`,
  SOURCE: (appUuid: string) => `${draftsV1Path}/${appUuid}/source`,
  SOURCE_COMPONENT: (appUuid: string, sourceComponent: string) =>
    `${draftsV1Path}/${appUuid}/source/${sourceComponent}`,
  PRISM_TAGS: `${prismV1Path}/tags`,
  PRISM_ARTIFACT_QUERY: `${prismV1Path}/artifacts/query`,
  IMPORT_PRISM_ARTIFACTS: `${prismV1Path}/import`,
  UPLOAD: `${appBuilderV1Path}/source/upload`,
  VALIDATE: (appUuid: string) => `${draftsV1Path}/${appUuid}/source/validate`,
  RESET: `${appBuilderV1Path}/source/reset`,
  SESSION_STATUS: (appUuid: string) => `${appBuilderV1Path}/${appUuid}/source/session`,
  UPDATE_SESSION_BASIS: (appUuid: string) => `${draftsV1Path}/${appUuid}/session`,
  TRACK_DEPLOY: `${appBuilderV1Path}/source/deploy`, // used to track deploy metrics, NOT for an actual app hub deploy
  METRICS: `${appBuilderV1Path}/metrics`,
  ZIP_UPLOAD: `${appBuilderV1Path}/source/archive`,
  APP_ZIP_UPLOAD: `${appsV1Path}/source/archive`,
  DEPLOY_APP: (appUuid: string) => `${appsV1Path}/${appUuid}/deploy`,
  AUTO_PROVISION_REFERENCE_AGENT: '/v1/agents/auto-provision',
  DOWNLOAD_APP: (appUuid: string, exclude?: ArchiveExcludeType[]) =>
    `${appBuilderV1Path}/${appUuid}/source/archive${getQueryParameters(exclude?.length ? { exclude } : undefined)}`,
  DOWNLOAD_VERSION: (appUuid: string, versionId: string, exclude?: ArchiveExcludeType[]) =>
    `${appBuilderV1Path}/${appUuid}/source/archive/version/${versionId}${getQueryParameters(
      exclude?.length ? { exclude } : undefined
    )}`,
  DOWNLOAD_BUILD: (appUuid: string, buildId: string, exclude?: ArchiveExcludeType[]) =>
    `${appBuilderV1Path}/${appUuid}/source/archive/build/${buildId}${getQueryParameters(
      exclude?.length ? { exclude } : undefined
    )}`,
  FORMAT_DOCUMENT: `${appBuilderV1Path}/source/format`,
  SCHEMA_ALL: (type: string) => `${appBuilderV1Path}/schemas/${type}`,
  SCHEMA: (type: string, id: string) => `${appBuilderV1Path}/schemas/${type}/${id}`,
  SCHEMA_LIST: (type: string) => `${appBuilderV1Path}/schemas/${type}SchemaIdentifiers`,
  LOGOUT: `${appBuilderV1Path}/logout`,
  METADATA: (namespace: string) => `${metadataV1Path}/${namespace}`,
  ASSETS: (appUuid: string, versionId: string) => `${appsV1Path}/${appUuid}/assets?versionId=${versionId}`,
  ASSETS_CONFIG: (appUuid: string, versionId: string) =>
    `${appsV1Path}/${appUuid}/assets/config?versionId=${versionId}`,
  NOTIF_TICKET: `${appBuilderV1Path}/notifications/ticket`,
  NOTIF_LISTEN: (ticket?: string) => `${appBuilderV1Path}/notifications/listen${ticket ? `?ticket=${ticket}` : ''}`,
  DRAFTS_ICON: (appUuid: string, filename: string) => `${draftsV1Path}/${appUuid}/assets/icon?filename=${filename}`,
  RENAME_DRAFTS_ICON: (appUuid: string, fileId: string) => `${draftsV1Path}/${appUuid}/assets/icon/rename/${fileId}`,
  WORKDAY_METADATA: (wmdName: string) => `${wmdV1Path}/${wmdName}`,
  LANGUAGE_SERVER_PROTOCOL: (appUuid: string, sessionType?: string) =>
    `/v1/sockets/editor/${appUuid}${sessionType ? `?sessionType=${sessionType}` : ''}`,
  TOKEN: `${devtoolsAuthV1Path}/token`,
  TOKEN_V2: `${devtoolsAuthV2Path}/token/developer`,
  TOKEN_SESSION_INFO: `${devtoolsAuthV1Path}/token/sessionInfo`,
  TENANT_TOKEN_DISCONNECT: `${devtoolsAuthV1Path}/token/tenant/disconnect`,
  TENANT_CLIENT_ID: `${devtoolsClientV1Path}/tenant`,
};

/**
 * A keen eye will notice that the endpoint versioning is not consistent. And you're right!
 * This was due to an oversight in the versioning location of the App Builder endpoint and will be updated at a later time.
 * We're not 100% sure that Copilot will continue to live in DevToolsServer so we're not going to update it until we're sure.
 */
export const CopilotEndpoint = {
  CHAT: `${copilotV1Path}/chat`,
  CHAT_HISTORY: `${copilotV1Path}/chat/history`,
  CHAT_CLEAN_HISTORY: `${copilotV1Path}/chat/history/clear`,
  CHAT_FEEDBACK: `${copilotV1Path}/chat/feedback`,
  GENERATE: `${copilotV1Path}/generate`,
  GENERATE_PROMPT_LIBRARY: (appId: string) => `${copilotV1Path}/generate/promptLibrary?appId=${appId}`,
  GENERATE_HISTORY: (generateTypes: string, appId: string) =>
    `${copilotV1Path}/generate/history?generateTypes=${generateTypes}/&appId=${appId}`,
  CLEAN_GENERATE_HISTORY: (appId: string) => `${copilotV1Path}/generate/history/clear?appId=${appId}`,
  GENERATE_FEEDBACK: `${copilotV1Path}/generate/feedback`,
  METRICS: `${copilotV1Path}/metrics`,
};

/**
 * DeepAgent is a feature of agent-forge based on the skill.md convention.
 * It enables Workday Build users to create custom agents and skills using
 * standard tooling (markdown). The endpoint routes to the backend service
 * that executes these agents.
 */
export const DeepAgentEndpoint = {
  CHAT: `/v1/wdayagent/chat`,
};

export const DevAgentEndpoint = {
  INVOCATIONS: `${devAgentV1Path}/invocations`,
  CONVERSATIONS: `${devAgentV1Path}/conversations`,
  CONVERSATIONS_STREAM: `${devAgentV1Path}/conversations/stream`,
  PROMPT_LIBRARY: (route?: string, preview?: boolean) => {
    const params = [
      ...(route ? [`route=${encodeURIComponent(route)}`] : []),
      ...(preview ? [`preview=${preview}`] : []),
    ];
    return `${devAgentV1Path}/promptLibrary${params.length ? `?${params.join('&')}` : ''}`;
  },
  MESSAGES_STREAM: (conversationId: string) => `${devAgentV1Path}/conversations/${conversationId}/messages/stream`,
  HISTORY: (conversationId: string) => `${devAgentV1Path}/conversations/${conversationId}/history`,
  DELETE_CONVERSATION: (conversationId: string) => `${devAgentV1Path}/conversations/${conversationId}`,
  ACCEPT_DRAFT_CHANGES: (conversationId: string, draftId: string) =>
    `${devAgentV1Path}/conversations/${conversationId}/drafts/${draftId}/accept`,
  REJECT_DRAFT_CHANGES: (conversationId: string, draftId: string) =>
    `${devAgentV1Path}/conversations/${conversationId}/drafts/${draftId}/reject`,
  FEEDBACK: (conversationId: string, responseId: string) =>
    `${devAgentV1Path}/conversations/${conversationId}/responses/${responseId}/feedback`,
  TOOLS_LIST: (route?: string) => `${devAgentV1Path}/tools/list${route ? `?route=${encodeURIComponent(route)}` : ''}`,
  METRICS: `${devAgentV1Path}/metrics`,
  STOP_GENERATION: (conversationId: string, runId: string) =>
    `${devAgentV1Path}/conversations/${conversationId}/runs/${runId}/stop`,
};

/**
 * Models a Managed Agent may run on, served by Workday's Centralized Inference Service (CIS) with
 * the producer, family, pricing tier, and entitlement the Model dropdown needs. Gated on the
 * MODEL_PROVIDER_AVAILABLE flag and the DEVELOPERAGENT_DESKTOP_ACCESS permission; both absences
 * surface as a 403 (see dtsAuthenticationInterceptor). `includeRestricted` keeps models the org may not
 * use in the response (with `allowed: false`) so the dropdown can show them disabled instead of
 * omitting them.
 */
export const ModelProviderEndpoint = {
  CUSTOM_AGENT_MODELS: '/v1/custom-agent-models?includeRestricted=true',
};

export const OrchestrationBuilderEndpoint = {
  // orch ID is the same as name (filename without the extension)
  DELETE_COMPONENT: (appUuid: string, orchId: string) => `/application/${appUuid}/workflow/${orchId}`,
  GET_SESSION: (appUuid: string, orchId: string) => `/application/${appUuid}/workflow/${orchId}/session`,
  LOGOUT: '/logout',
};

export const StudioBuilderEndpoint = {
  GET_SESSION: '/application/session',
  DELETE_COLLECTION: (appUuid: string, collectionName: string) =>
    `/application/${appUuid}/collection/${collectionName}`,
};

export const DevSiteExecutor = new DevsiteRequestExecutor().withInterceptor(authenticationInterceptor);

// use with caution! octo returns 403 when a user is authorized BUT the company UUID doesn't exist so this is a necessary workaround when that is a valid case for your app
export const OctopaasExecutorWithoutAuthRedirect = new RegionalRequestExecutor<string>(ExtendServicePath.OCTOPAAS, {
  authorization: { type: 'ID' },
});

export const OctopaasExecutor = new RegionalRequestExecutor<string>(ExtendServicePath.OCTOPAAS, {
  authorization: { type: 'ID' },
}).withInterceptor(authenticationInterceptor);

export const RobotExecutor = new RegionalRequestExecutor<string>(ExtendServicePath.ROBOT, {
  authorization: {
    type: 'Bearer',
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    customAuthFetcher: crossTeamToggles.TOGGLE_GLOBAL_CONNECT_TO_TENANT
      ? getTenantAuthTokenWithOrgId
      : getTenantAuthToken,
  },
}).withInterceptor(authenticationInterceptor);

export const MaltExecutor = new MaltRequestExecutor<string>(ExtendSubdomainService.ANALYTICS);

export const CMSExecutor = new RequestExecutor<string>('/cms').withInterceptor(authenticationInterceptor);

export const ReqExecutor = new RequestExecutor<string>();

// changing something? Make sure you check session.ts as well, there's another devsite api executor in there.
export const DevsiteApiExecutor = new RequestExecutor<string>('/api', {
  defaultHeaders: { [HeaderKeys.CONTENT_TYPE]: MimeType.JSON },
  csrfToken: session.getCsrfToken,
}).withInterceptor(authenticationInterceptor);

export const MfeExecutor = new RequestExecutor<string>('/bundles', {
  defaultHeaders: { [HeaderKeys.CACHE_CONTROL]: 'no-store, no-cache, must-revalidate, proxy-revalidate' },
});

export const ApphubExecutor = new RegionalRequestExecutor<string>(ExtendServicePath.APPHUB, {
  authorization: { type: 'Bearer' },
}).withInterceptor(authenticationInterceptor);

export const AosExecutor = new RegionalRequestExecutor<string>(ExtendServicePath.AOS, {
  authorization: { type: 'Bearer', header: HeaderKeys.WCP_AUTHORIZATION },
}).withInterceptor(aosAuthenticationInterceptor);

export const AsorInternalExecutor = new RegionalRequestExecutor<string>(
  ExtendServicePath.ASOR_INTERNAL,
  {}
).withInterceptor(aosAuthenticationInterceptor);

/**
 * IMPORTANT: Every usage requires an Org-Id header to be set (see other usages for examples).
 */
export const OrchestrationBuilderExecutor = new OrchestrationBuilderRequestExecutor<string>(
  ExtendSubdomainService.ORCH_BUILDER,
  {
    authorization: { type: 'Bearer' },
  }
).withInterceptor(authenticationInterceptor);

export const StudioBuilderExecutor = new StudioBuilderRequestExecutor<string>(ExtendSubdomainService.STUDIOBUILDER, {
  authorization: { type: 'Bearer' },
}).withInterceptor(authenticationInterceptor);
